import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { LogoLockup } from "./Logo";
import { CtaButton } from "./CtaButton";
import { scrollToSection } from "./SmoothScroll";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") navigate(id === "top" ? "/" : `/#${id}`);
    else scrollToSection(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled || open ? "bg-cream/85 backdrop-blur-xl shadow-[0_8px_30px_-20px_rgba(18,52,90,0.35)]" : "bg-transparent"}`}
    >
      <nav aria-label="Main" className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="/" onClick={(e) => go(e, "top")} data-testid="nav-logo-link" aria-label="KeepThis! home" className="rounded-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40">
          <LogoLockup />
        </a>
        <ul className="hidden items-center gap-9 md:flex">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <a href={`/#${l.id}`} onClick={(e) => go(e, l.id)} data-testid={l.testId} className="nav-link font-heading text-[15px] font-bold text-navy">
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="hidden md:block">
          <CtaButton testId="nav-get-started-button" source="nav" label="Get Started" size="sm" />
        </div>
        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-cream md:hidden focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>
      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-navy/10 px-5 pb-6 pt-2 md:hidden">
          <ul className="flex flex-col">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <a href={`/#${l.id}`} onClick={(e) => go(e, l.id)} data-testid={`mobile-${l.testId}`} className="block py-3.5 font-heading text-xl font-extrabold text-navy">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <CtaButton testId="mobile-nav-get-started-button" source="nav-mobile" label="Get Started" className="mt-3 w-full" />
        </div>
      )}
    </header>
  );
};
